// src/pages/bloomterminal/components/dashboard/withChartData.jsx
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import Colors from '../../../constants/Colors';
import PageSkeleton from '../../../components/PageSkeleton';

/**
 * Higher-Order Component that wraps a chart component with data fetching
 * 
 * Creates a self-contained chart container that:
 * - Fetches data using the provided hook
 * - Transforms the data using the provided transform function
 * - Handles loading and error states
 * - Renders the chart with transformed data
 * 
 * @param {React.Component} ChartComponent - The chart component to render
 * @param {Function} useDataHook - The React Query hook to fetch data
 * @param {Function} transformFn - Function to transform raw API data
 * @param {string} dataKey - Key to extract data from hook result
 * @returns {React.Component} - A wrapped chart component with data fetching
 * 
 * @example
 * const PriceChartContainer = withChartData(
 *   PriceLineChart,
 *   usePriceChartData,
 *   preparePricesData,
 *   'priceChart'
 * );
 */
const withChartData = (ChartComponent, useDataHook, transformFn, dataKey) => {
  // Create and return a new component
  const ChartWithData = ({ apiParams, ...restProps }) => {
    const { t } = useTranslation();
    const { email, commodity, dateStart, dateEnd } = apiParams || {};

    // Call the data hook - each hook handles its own params internally
    const hookResult = useDataHook(email, commodity, dateStart, dateEnd);

    const { isLoading, isError, error } = hookResult;
    const rawData = hookResult[dataKey] || hookResult.data || [];
    const hasChartRows = Array.isArray(rawData) && rawData.length > 0;

    // Extract additional props that charts may need
    const origins = hookResult.origins || [];
    const years = hookResult.years || [];
    const filterOptions = hookResult.filterOptions ?? null;

    // Transform data using the provided transform function
    const transformedData = useMemo(() => {
      if (!rawData || !transformFn) {
        return rawData || [];
      }
      return transformFn(rawData);
    }, [rawData]);

    // Loading state — keep chart mounted during refetch if we still have rows (avoids remount → hydrate stomping local filters)
    if (isLoading && !hasChartRows) {
      return <PageSkeleton shape="chart" />;
    }

    // Error state
    if (isError) {
      return (
        <div className="h-full w-full flex flex-col items-center justify-center gap-3 px-4">
          <AlertCircle
            size={32}
            style={{ color: Colors.error || '#ef4444' }}
          />
          <p className="text-gray-400 text-sm text-center">
            {error?.message || t('charts.errorLoading')}
          </p>
        </div>
      );
    }

    // Render chart with data and additional props (commodity: stable key for widgets that must not reset on unrelated re-renders)
    return (
      <ChartComponent
        data={transformedData}
        email={email}
        commodity={commodity}
        origins={origins}
        years={years}
        filterOptions={filterOptions}
        dateStart={dateStart}
        dateEnd={dateEnd}
        commodity={commodity}
        {...restProps}
      />
    );
  };

  // Set display name for debugging
  ChartWithData.displayName = `withChartData(${ChartComponent.displayName || ChartComponent.name || 'Component'})`;

  return ChartWithData;
};

export default withChartData;

